import { householdApiRequest } from './household-sharing';

const CONTRACT_VERSION = 'frozen-save.web-push-reminder.v1';

type ReminderFrequency = 'daily' | 'weekly';
type ReminderLocale = 'en' | 'fr-FR';

interface ReminderConfiguration {
  readonly contractVersion: typeof CONTRACT_VERSION;
  readonly publicKey: string;
}

const applicationServerKey = (value: string): Uint8Array<ArrayBuffer> => {
  if (!/^[A-Za-z0-9_-]{80,120}$/u.test(value)) throw new Error('REMINDER_CONFIGURATION_INVALID');
  const padded = `${value.replaceAll('-', '+').replaceAll('_', '/')}${'='.repeat((4 - value.length % 4) % 4)}`;
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
};

const subscriptionPayload = (subscription: PushSubscription) => {
  const value = subscription.toJSON();
  if (
    typeof value.endpoint !== 'string' || value.keys == null ||
    typeof value.keys.p256dh !== 'string' || typeof value.keys.auth !== 'string'
  ) throw new Error('REMINDER_SUBSCRIPTION_INVALID');
  return {
    endpoint: value.endpoint,
    expirationTime: null,
    keys: { auth: value.keys.auth, p256dh: value.keys.p256dh },
  };
};

export class WebPushReminderAdapter {
  readonly available =
    'Notification' in globalThis && 'serviceWorker' in navigator && 'PushManager' in globalThis;

  async subscribe(frequency: ReminderFrequency, locale: ReminderLocale): Promise<boolean> {
    if (!this.available || Notification.permission !== 'granted') return false;
    const registration = await navigator.serviceWorker.ready;
    const configuration = await householdApiRequest<ReminderConfiguration>('/reminders/config');
    if (configuration.contractVersion !== CONTRACT_VERSION) {
      throw new Error('REMINDER_CONFIGURATION_INVALID');
    }
    const existing = await registration.pushManager.getSubscription();
    const subscription = existing ?? await registration.pushManager.subscribe({
      applicationServerKey: applicationServerKey(configuration.publicKey),
      userVisibleOnly: true,
    });
    await householdApiRequest('/reminders/subscribe', {
      body: {
        contractVersion: CONTRACT_VERSION,
        frequency,
        locale,
        subscription: subscriptionPayload(subscription),
      },
    });
    return true;
  }

  async unsubscribe(): Promise<void> {
    if (!this.available) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription == null) return;
    await householdApiRequest('/reminders/unsubscribe', {
      body: { contractVersion: CONTRACT_VERSION, endpoint: subscription.endpoint },
    });
    await subscription.unsubscribe();
  }
}

export { CONTRACT_VERSION as WEB_PUSH_REMINDER_CONTRACT };
